import type { TFunction } from 'i18next';

import type { MailrailBaseEmailCampaign } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';

export type TConfirmVariant = 'unsaved-navigation' | 'live-save' | 'republish';

export interface IModalCopy {
  title: string;
  /** Body sentence for the unsaved-navigation variant. */
  body?: string;
  alertTitle?: string;
  alertDescription?: string;
  /** Confirm button label for live-save / republish. */
  confirmButtonText?: string;
  /** Secondary "discard and leave" button label for unsaved-navigation. */
  discardButtonText?: string;
  cancelButtonText: string;
}

/** "This campaign is currently stopped" / "This campaign has ended" depending on state. */
function getStatusTitle(campaign: MailrailBaseEmailCampaign, t: TFunction): string {
  return campaign.isComplete
    ? t('This campaign has ended')
    : t('This campaign is currently stopped');
}

export function getModalCopy(
  variant: TConfirmVariant,
  campaign: MailrailBaseEmailCampaign,
  t: TFunction,
): IModalCopy {
  const cancelButtonText = t('Cancel');

  if (variant === 'live-save') {
    return {
      title: t('Save changes to a live campaign?'),
      alertTitle: t('This campaign is live'),
      alertDescription: t(
        'It is currently published and sending. Your changes take effect immediately, but are not retroactive on emails that were already sent.',
      ),
      confirmButtonText: t('Save changes'),
      cancelButtonText,
    };
  }

  if (variant === 'republish') {
    return {
      title: t('Re-publish this campaign?'),
      alertTitle: getStatusTitle(campaign, t),
      alertDescription: t(
        "Re-publishing applies any changes you've made and resumes sending to the campaign's audience.",
      ),
      confirmButtonText: t('Publish'),
      cancelButtonText,
    };
  }

  // variant == 'unsaved-navigation'
  const defaultBody = t(
    'You have unsaved changes to this campaign or its template(s). Save them before you leave?',
  );
  const bodyMap: Partial<Record<CampaignState, string>> = {
    [CampaignState.Complete]: t(
      "This campaign has ended. To keep your changes you'll need to re-publish it. Re-publish now, or leave without saving?",
    ),
    [CampaignState.Stopped]: t(
      "This campaign is currently stopped. To keep your changes you'll need to re-publish it. Re-publish now, or leave without saving?",
    ),
  };
  return {
    title: t('Unsaved changes'),
    body: bodyMap[campaign.get('state')] ?? defaultBody,
    alertTitle: campaign.isPublished ? t('This is a live campaign') : undefined,
    alertDescription: campaign.isPublished
      ? t('Saving applies your changes to a campaign that is currently running.')
      : undefined,
    discardButtonText: t('Discard changes'),
    cancelButtonText,
  };
}
